import React, { useState } from 'react'
import { Settings, Volume2, Gauge, Mic2, Globe, User } from 'lucide-react'
import { useApp } from '../context/AppContext.jsx'
import VoicePreview from '../components/VoicePreview.jsx'

function VoiceSettings() {
  const { voiceSettings, updateVoiceSettings } = useApp()
  const [showPreview, setShowPreview] = useState(false)

  const accents = [
    { value: 'en-US', label: 'American English', flag: '🇺🇸' },
    { value: 'en-GB', label: 'British English', flag: '🇬🇧' },
    { value: 'en-AU', label: 'Australian English', flag: '🇦🇺' },
    { value: 'en-IN', label: 'Indian English', flag: '🇮🇳' },
    { value: 'en-CA', label: 'Canadian English', flag: '🇨🇦' },
    { value: 'en-ZA', label: 'South African English', flag: '🇿🇦' },
    { value: 'es-ES', label: 'Spanish (Spain)', flag: '🇪🇸' },
    { value: 'es-MX', label: 'Spanish (Mexico)', flag: '🇲🇽' },
    { value: 'fr-FR', label: 'French (France)', flag: '🇫🇷' },
    { value: 'de-DE', label: 'German', flag: '🇩🇪' },
    { value: 'it-IT', label: 'Italian', flag: '🇮🇹' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)', flag: '🇧🇷' },
    { value: 'ja-JP', label: 'Japanese', flag: '🇯🇵' },
    { value: 'ko-KR', label: 'Korean', flag: '🇰🇷' },
    { value: 'zh-CN', label: 'Chinese (Mandarin)', flag: '🇨🇳' },
    { value: 'ru-RU', label: 'Russian', flag: '🇷🇺' }
  ]

  const handleSettingChange = (setting, value) => {
    updateVoiceSettings({ [setting]: value })
  }

  const resetToDefaults = () => {
    updateVoiceSettings({
      accent: 'en-US',
      gender: 'female',
      speed: 1,
      pitch: 1,
      volume: 1
    })
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Voice Settings
        </h1>
        <p className="text-gray-600">
          Customize your voice preferences for the perfect speech output.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Language & Accent */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Language & Accent
                </h2>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Accent
                </label>
                <select
                  value={voiceSettings.accent}
                  onChange={(e) => handleSettingChange('accent', e.target.value)}
                  className="input-field"
                >
                  {accents.map((accent) => (
                    <option key={accent.value} value={accent.value}>
                      {accent.flag} {accent.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Voice Gender */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Voice Gender
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleSettingChange('gender', 'female')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${voiceSettings.gender === 'female'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">👩</div>
                  <div className="font-medium">Female</div>
                </div>
              </button>

              <button
                onClick={() => handleSettingChange('gender', 'male')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${voiceSettings.gender === 'male'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">👨</div>
                  <div className="font-medium">Male</div>
                </div>
              </button>
            </div>
          </div>

          {/* Voice Controls */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <Gauge className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Voice Controls
                </h2>
              </div>
            </div>

            <div className="space-y-6">
              {/* Speed Control */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Speech Speed
                  </label>
                  <span className="text-sm text-gray-600">
                    {voiceSettings.speed.toFixed(1)}x
                  </span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={voiceSettings.speed}
                  onChange={(e) => handleSettingChange('speed', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Slow (0.5x)</span>
                  <span>Normal (1x)</span>
                  <span>Fast (2x)</span>
                </div>
              </div>

              {/* Pitch Control */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Voice Pitch
                  </label>
                  <span className="text-sm text-gray-600">
                    {voiceSettings.pitch.toFixed(1)}
                  </span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={voiceSettings.pitch}
                  onChange={(e) => handleSettingChange('pitch', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Low (0.5)</span>
                  <span>Normal (1)</span>
                  <span>High (2)</span>
                </div>
              </div>

              {/* Volume Control */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Volume
                  </label>
                  <span className="text-sm text-gray-600">
                    {Math.round(voiceSettings.volume * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={voiceSettings.volume}
                  onChange={(e) => handleSettingChange('volume', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Mute (0%)</span>
                  <span>Half (50%)</span>
                  <span>Full (100%)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Voice Settings
                </h3>
                <p className="text-sm text-gray-600">
                  Test your current settings or reset to defaults
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowPreview(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Mic2 className="w-4 h-4" />
                  Test Voice
                </button>
                <button
                  onClick={resetToDefaults}
                  className="btn-secondary"
                >
                  Reset Defaults
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Summary */}
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                Current Settings
              </h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Language</span>
                <span className="text-sm font-medium">
                  {accents.find(a => a.value === voiceSettings.accent)?.label}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Gender</span>
                <span className="text-sm font-medium capitalize">
                  {voiceSettings.gender}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Speed</span>
                <span className="text-sm font-medium">
                  {voiceSettings.speed.toFixed(1)}x
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Pitch</span>
                <span className="text-sm font-medium">
                  {voiceSettings.pitch.toFixed(1)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Volume</span>
                <span className="text-sm font-medium">
                  {Math.round(voiceSettings.volume * 100)}%
                </span>
              </div>
            </div>
          </div>

          {/* Voice Quality Tips */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">
                Quality Tips
              </h3>
            </div>

            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>Use normal speed (1x) for best clarity</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>Adjust pitch based on content type</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>Test different accents for variety</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>Keep volume at 80-100% for recordings</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Voice Preview Modal */}
      {showPreview && (
        <VoicePreview
          settings={voiceSettings}
          onClose={() => setShowPreview(false)}
        />
      )}
      {/* Voice Optimization Guide */}
      <div className="mt-20 pt-16 border-t border-gray-200">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mastering Voice Customization</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="card bg-gradient-to-b from-white to-primary-50">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Gauge className="w-5 h-5 text-primary-600" />
              Impact of Speed
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Adjusting the speech rate is crucial for different content types.
              <strong> 1.0x (Normal)</strong> is ideal for general listening and news.
              <strong> 0.8x to 0.9x</strong> is recommended for educational materials or
              technical tutorials where clarity is paramount.
              <strong> 1.1x to 1.2x</strong> is great for quick updates or
              social media content where high energy is needed.
            </p>
          </div>
          <div className="card bg-gradient-to-b from-white to-accent-50">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Mic2 className="w-5 h-5 text-accent-600" />
              Pitch and Emotion
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Pitch significantly alters the "personality" of the voice.
              A <strong>lower pitch</strong> can sound more authoritative and soothing,
              making it perfect for audiobooks or corporate narration.
              A <strong>higher pitch</strong> often creates a more youthful and
              enthusiastic tone, suitable for children's stories or
              cheerful product announcements.
            </p>
          </div>
          <div className="card bg-gradient-to-b from-white to-green-50">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-green-600" />
              Global Reach
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              With over 15+ regional accents, you can localize your content
              for audiences around the world. Selecting the right accent
              ensures your message resonates better with local listeners.
              From the crisp tones of British English to the warm resonances
              of Australian accents, the right selection makes a powerful difference.
            </p>
          </div>
        </div>

        <div className="mt-12 p-8 bg-gray-900 text-white rounded-3xl">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">Why Customize Your AI Voice?</h3>
            <p className="text-gray-300 leading-relaxed">
              In the age of digital content, a robotic voice is no longer enough.
              Modern listeners expect high-quality, natural-sounding audio that
              captures the essence of human communication. By fine-tuning the
              speed, pitch, and accent, you breathe life into your text, making it
              more engaging and professional. Whether you're an accessibility
              advocate or a digital marketer, the ability to control these
              nuances gives you a creative edge in reaching your audience
              effectively.
            </p>
            <div className="mt-8 flex justify-center gap-4">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-primary-400 rounded-full"></span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Neural Synthesis</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-accent-400 rounded-full"></span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Natural Prosody</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Low Latency</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VoiceSettings
